import { getServerUrl } from "../config/server-config";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_COOKIE_KEY,
} from "./token-store";

const REFRESH_AHEAD_SECONDS = 5 * 60;

type RefreshResponse = {
  success?: boolean;
  obj?: {
    accessToken?: string;
    refreshToken?: string;
  };
};

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(json) as { exp?: number };
  } catch {
    return null;
  }
}

export function isTokenExpiredOrNearExpiry(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + REFRESH_AHEAD_SECONDS;
}

export function mergeCookieHeader(
  rawCookie: string | null,
  accessToken: string,
  refreshToken: string,
): string {
  const cookiePairs = (rawCookie ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter(
      (part) =>
        !part.startsWith(`${ACCESS_TOKEN_COOKIE_KEY}=`) &&
        !part.startsWith(`${REFRESH_TOKEN_COOKIE_KEY}=`),
    );
  cookiePairs.push(
    `${ACCESS_TOKEN_COOKIE_KEY}=${encodeURIComponent(accessToken)}`,
  );
  cookiePairs.push(
    `${REFRESH_TOKEN_COOKIE_KEY}=${encodeURIComponent(refreshToken)}`,
  );
  return cookiePairs.join("; ");
}

export async function refreshTokens(
  oldAccessToken: string,
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken: string } | null> {
  try {
    const baseUrl = getServerUrl();
    const response = await fetch(
      `${baseUrl}/v1/auth/refresh?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(oldAccessToken)}`,
      { method: "POST" },
    );
    if (!response.ok) return null;

    const data = (await response.json()) as RefreshResponse;
    const nextAccessToken = data.obj?.accessToken;
    const nextRefreshToken = data.obj?.refreshToken || refreshToken;
    if (!data.success || !nextAccessToken) return null;

    return { accessToken: nextAccessToken, refreshToken: nextRefreshToken };
  } catch {
    return null;
  }
}
